import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, rolesApi, homeCardsApi, tenantsApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import {
  ArrowLeft, Save, User2, Shield, Smartphone,
  Eye, EyeOff, CheckCircle2, XCircle, Info,
} from 'lucide-react'
const PERMISSION_LABELS: Record<string, string> = {
  '*': 'Acesso total ao sistema',
  'users.*': 'Gestão completa de utilizadores',
  'users.read': 'Ver utilizadores',
  'users.create': 'Criar utilizadores',
  'users.update': 'Editar utilizadores',
  'users.delete': 'Eliminar utilizadores',
  'forms.*': 'Gestão completa de formulários',
  'forms.read': 'Ver formulários',
  'forms.create': 'Criar formulários',
  'form_submissions.*': 'Gestão de submissões',
  'form_submissions.read': 'Ver submissões',
  'form_submissions.create': 'Criar submissões',
  'devices.*': 'Gestão de dispositivos',
  'devices.read': 'Ver dispositivos',
  'tasks.*': 'Gestão de tarefas',
  'tasks.read': 'Ver tarefas',
  'reports.read': 'Ver relatórios',
  'home_cards.*': 'Gestão de cards mobile',
  'releases.*': 'Gestão de versões APK',
}

function labelPerm(p: string) {
  return PERMISSION_LABELS[p] ?? p
}

// ── Tab definitions ────────────────────────────────────────────────────────
type Tab = 'basic' | 'roles' | 'mobile'
const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'basic',  label: 'Dados Básicos',       icon: <User2 className="h-4 w-4" /> },
  { id: 'roles',  label: 'Roles & Permissões',  icon: <Shield className="h-4 w-4" /> },
  { id: 'mobile', label: 'Acesso Mobile',        icon: <Smartphone className="h-4 w-4" /> },
]

// ── Main component ─────────────────────────────────────────────────────────
export default function UserFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user: authUser } = useAuth()
  const isSuperAdmin = authUser?.roles?.some(r => r.name === 'SUPER_ADMIN') ||
    authUser?.permissions?.includes('*') || false

  const [selectedTenantId, setSelectedTenantId] = useState<string>('')

  const [tab, setTab] = useState<Tab>('basic')
  const [showPwd, setShowPwd] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMsg, setSuccessMsg] = useState('')

  // Form state
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    whatsapp: '',
    password: '',
    is_active: true,
    is_verified: false,
    mobile_access: true,
  })
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])

  // ── Fetch existing user (edit mode) ────────────────────────────────────
  const { data: userData, isError: userError } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.get(Number(id)),
    enabled: isEdit,
    retry: false,
  })

  useEffect(() => {
    if (userData?.data) {
      const u = userData.data
      setForm({
        email: u.email,
        first_name: u.first_name,
        last_name: u.last_name,
        phone: u.phone ?? '',
        whatsapp: u.whatsapp ?? '',
        password: '',
        is_active: u.is_active,
        is_verified: u.is_verified,
        mobile_access: u.mobile_access,
      })
      setSelectedRoleIds(u.roles.map(r => r.id))
    }
  }, [userData])

  // ── Fetch tenants (SUPER_ADMIN only, for user creation) ───────────────
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.list({ page: 1, page_size: 100 }),
    enabled: isSuperAdmin && !isEdit,
  })
  const allTenants = tenantsData?.data?.items ?? []

  // ── Fetch available roles ───────────────────────────────────────────────
  const { data: allRoles = [], isLoading: rolesLoading, isError: rolesError } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list().then(r => r.data),
  })

  // ── Fetch home cards (for mobile preview) ──────────────────────────────
  const { data: cardsData } = useQuery({
    queryKey: ['home-cards-admin'],
    queryFn: homeCardsApi.listAdmin,
  })
  const allCards = cardsData?.data ?? []

  // ── Compute effective permissions from selected roles ─────────────────
  const effectivePermissions = Array.from(
    new Set(
      allRoles
        .filter(r => selectedRoleIds.includes(r.id))
        .flatMap(r => r.permissions)
    )
  ).sort()

  // Cards user will see (has wildcard or matching permission)
  const visibleCards = allCards.filter(c => {
    if (!c.is_active) return false
    if (!c.required_permission) return true
    if (effectivePermissions.includes('*')) return true
    return effectivePermissions.includes(c.required_permission)
  })

  // ── Save mutation ───────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone || undefined,
        whatsapp: form.whatsapp || undefined,
        is_active: form.is_active,
        is_verified: form.is_verified,
        mobile_access: form.mobile_access,
        role_ids: selectedRoleIds,
        ...(isEdit
          ? { new_password: form.password || undefined }
          : { password: form.password }),
        ...(!isEdit && isSuperAdmin && selectedTenantId
          ? { tenant_id: Number(selectedTenantId) }
          : {}),
      }
      if (isEdit) return usersApi.update(Number(id), payload as any)
      return usersApi.create(payload as any)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      if (isEdit) {
        setSuccessMsg('Utilizador actualizado com sucesso!')
        setTimeout(() => setSuccessMsg(''), 3000)
      } else {
        navigate('/users')
      }
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail
      if (typeof detail === 'string') setErrors({ _global: detail })
      else if (Array.isArray(detail)) {
        const errs: Record<string, string> = {}
        detail.forEach((d: any) => {
          const field = d.loc?.[d.loc.length - 1]
          if (field) errs[field] = d.msg
        })
        setErrors(errs)
      }
    },
  })

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!form.email) e.email = 'Email obrigatório'
    if (!form.first_name) e.first_name = 'Primeiro nome obrigatório'
    if (!form.last_name) e.last_name = 'Apelido obrigatório'
    if (!isEdit && !form.password) e.password = 'Password obrigatória'
    if (!isEdit && form.password && form.password.length < 8)
      e.password = 'Password deve ter pelo menos 8 caracteres'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) { setTab('basic'); return }
    saveMutation.mutate()
  }

  function set(field: string, value: any) {
    setForm(f => ({ ...f, [field]: value }))
  }

  function toggleRole(roleId: number) {
    setSelectedRoleIds(ids =>
      ids.includes(roleId) ? ids.filter(i => i !== roleId) : [...ids, roleId]
    )
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (isEdit && userError) {
    return (
      <div className="max-w-3xl">
        <div className="mb-6 flex items-center gap-4">
          <button onClick={() => navigate('/users')} className="rounded-lg p-2 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Editar Utilizador</h1>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
          <p className="text-lg font-medium text-red-700">Utilizador não encontrado</p>
          <p className="mt-1 text-sm text-red-500">O utilizador com ID {id} não existe ou não pertence a este tenant.</p>
          <button
            onClick={() => navigate('/users')}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Voltar à lista
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl">
      {/* Back + title */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/users')} className="rounded-lg p-2 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Editar Utilizador' : 'Novo Utilizador'}
          </h1>
          {isEdit && userData?.data && (
            <p className="text-sm text-gray-500">
              {userData.data.email}
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex border-b border-gray-200">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Global error / success */}
      {errors._global && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {errors._global}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {successMsg}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── TAB: Dados Básicos ──────────────────────────────────────────── */}
        {tab === 'basic' && (
          <div className="card space-y-5">
            {/* Tenant selector — only SUPER_ADMIN on create */}
            {isSuperAdmin && !isEdit && allTenants.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant de destino
                </label>
                <select
                  className="input w-full"
                  value={selectedTenantId}
                  onChange={e => setSelectedTenantId(e.target.value)}
                >
                  <option value="">Meu tenant (padrão)</option>
                  {allTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">Deixar em branco cria o utilizador no seu tenant</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Primeiro Nome *" error={errors.first_name}>
                <input
                  className={`input w-full ${errors.first_name ? 'border-red-400' : ''}`}
                  value={form.first_name}
                  onChange={e => set('first_name', e.target.value)}
                  placeholder="João"
                />
              </Field>
              <Field label="Apelido *" error={errors.last_name}>
                <input
                  className={`input w-full ${errors.last_name ? 'border-red-400' : ''}`}
                  value={form.last_name}
                  onChange={e => set('last_name', e.target.value)}
                  placeholder="Silva"
                />
              </Field>
            </div>

            <Field label="Email *" error={errors.email}>
              <input
                type="email"
                className={`input w-full ${errors.email ? 'border-red-400' : ''}`}
                value={form.email}
                onChange={e => set('email', e.target.value)}
                placeholder="joao.silva@empresa.pt"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Telemóvel">
                <input
                  className="input w-full"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="+351 9XX XXX XXX"
                />
              </Field>
              <Field label="WhatsApp">
                <input
                  className="input w-full"
                  value={form.whatsapp}
                  onChange={e => set('whatsapp', e.target.value)}
                  placeholder="+351 9XX XXX XXX"
                />
              </Field>
            </div>

            <Field
              label={isEdit ? 'Nova Password (deixar em branco para manter)' : 'Password *'}
              error={errors.password}
            >
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  className={`input w-full pr-10 ${errors.password ? 'border-red-400' : ''}`}
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={isEdit ? '••••••••' : 'Mínimo 8 caracteres'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            {/* Toggles */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <Toggle
                label="Conta ativa"
                description="Utilizador pode iniciar sessão no sistema"
                value={form.is_active}
                onChange={v => set('is_active', v)}
              />
              <Toggle
                label="Email verificado"
                description="Ignorar verificação de email"
                value={form.is_verified}
                onChange={v => set('is_verified', v)}
              />
            </div>
          </div>
        )}

        {/* ── TAB: Roles & Permissões ────────────────────────────────────── */}
        {tab === 'roles' && (
          <div className="space-y-4">
            <div className="card">
              <h3 className="mb-1 font-semibold text-gray-900">Roles do utilizador</h3>
              <p className="mb-4 text-sm text-gray-500">
                Selecione os roles que este utilizador terá. As permissões são a união de todos os roles selecionados.
              </p>
              <div className="space-y-2">
                {rolesLoading && (
                  <p className="text-sm text-gray-400">A carregar roles...</p>
                )}
                {rolesError && (
                  <p className="text-sm text-red-500">Erro ao carregar roles. Recarregue a página.</p>
                )}
                {!rolesLoading && !rolesError && allRoles.length === 0 && (
                  <p className="text-sm text-gray-400">Sem roles disponíveis.</p>
                )}
                {allRoles.map(role => {
                  const selected = selectedRoleIds.includes(role.id)
                  return (
                    <label
                      key={role.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        selected
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                        checked={selected}
                        onChange={() => toggleRole(role.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{role.name}</span>
                          {role.is_system && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                              sistema
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <p className="mt-0.5 text-xs text-gray-500">{role.description}</p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {role.permissions.slice(0, 6).map(p => (
                            <span key={p} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600">
                              {p}
                            </span>
                          ))}
                          {role.permissions.length > 6 && (
                            <span className="text-xs text-gray-400">+{role.permissions.length - 6} mais</span>
                          )}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Effective permissions summary */}
            {effectivePermissions.length > 0 && (
              <div className="card">
                <h3 className="mb-3 font-semibold text-gray-900 flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-500" />
                  Permissões efectivas ({effectivePermissions.length})
                </h3>
                <div className="space-y-1.5">
                  {effectivePermissions.map(p => (
                    <div key={p} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                      <div>
                        <span className="font-mono text-gray-700">{p}</span>
                        <span className="ml-2 text-gray-400">— {labelPerm(p)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedRoleIds.length === 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                Nenhum role selecionado — o utilizador não terá acesso a funcionalidades do sistema.
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Acesso Mobile ─────────────────────────────────────────── */}
        {tab === 'mobile' && (
          <div className="space-y-4">
            <div className="card">
              <h3 className="mb-1 font-semibold text-gray-900">Acesso à aplicação mobile</h3>
              <p className="mb-4 text-sm text-gray-500">
                Controla se este utilizador pode fazer login na aplicação Android/iOS.
              </p>
              <Toggle
                label="Permitir acesso mobile"
                description="Utilizador pode usar a app Shopfloor Hub no telemóvel"
                value={form.mobile_access}
                onChange={v => set('mobile_access', v)}
                size="lg"
              />
            </div>

            {/* Card preview */}
            <div className="card">
              <h3 className="mb-1 font-semibold text-gray-900 flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-gray-500" />
                Cards visíveis no ecrã inicial
              </h3>
              <p className="mb-4 text-sm text-gray-500">
                Baseado nos roles selecionados — um card é mostrado se a permissão necessária estiver activa.
              </p>

              {!form.mobile_access ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Acesso mobile desativado — o utilizador não verá nenhum card.
                </div>
              ) : allCards.length === 0 ? (
                <p className="text-sm text-gray-400">A carregar cards...</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {allCards.map(card => {
                    const visible = visibleCards.some(c => c.id === card.id)
                    return (
                      <div
                        key={card.id}
                        className={`flex items-center gap-3 rounded-lg border p-3 ${
                          visible
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-200 bg-gray-50 opacity-50'
                        }`}
                      >
                        <div
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: card.color_hex }}
                        >
                          {card.title[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{card.title}</p>
                          {card.required_permission ? (
                            <p className="text-xs font-mono text-gray-400 truncate">{card.required_permission}</p>
                          ) : (
                            <p className="text-xs text-gray-400">Público</p>
                          )}
                        </div>
                        {visible
                          ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-500" />
                          : <XCircle className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                      </div>
                    )
                  })}
                </div>
              )}

              {form.mobile_access && allCards.length > 0 && (
                <p className="mt-3 text-xs text-gray-400">
                  {visibleCards.length} de {allCards.filter(c => c.is_active).length} cards ativos visíveis
                  {' '}• Para alterar quais cards existem, vai a <strong>Home Cards</strong>
                  {' '}• Para alterar as permissões, ajusta os <strong>Roles</strong>
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Save button ───────────────────────────────────────────────── */}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="btn btn-primary"
          >
            {saveMutation.isPending ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                A guardar...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {isEdit ? 'Guardar alterações' : 'Criar utilizador'}
              </span>
            )}
          </button>
        </div>

      </form>
    </div>
  )
}

// ── Helper components ──────────────────────────────────────────────────────

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

function Toggle({
  label,
  description,
  value,
  onChange,
  size = 'sm',
}: {
  label: string
  description?: string
  value: boolean
  onChange: (v: boolean) => void
  size?: 'sm' | 'lg'
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className={`font-medium text-gray-900 ${size === 'lg' ? 'text-base' : 'text-sm'}`}>
          {label}
        </p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
          value ? 'bg-primary-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            value ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

